import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

/**
 * GDPR-compliant cookie consent banner.
 *
 * - Loads GA4 + Facebook Pixel ONLY after the user accepts.
 * - Stores consent in localStorage as JSON: { analytics: bool, marketing: bool, ts: ISO }
 * - Shows again only if consent has not been set yet.
 */

const STORAGE_KEY = "mf_cookie_consent_v1";
const GA_MEASUREMENT_ID = "G-9E09CBDTNV";
const FB_PIXEL_ID = "1652870675828227";

function loadGA4() {
  if (window.__ga4_loaded) return;
  window.__ga4_loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });
}

function loadFacebookPixel() {
  if (window.__fbq_loaded) return;
  window.__fbq_loaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq && f.fbq.loaded) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = n.queue || [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", FB_PIXEL_ID);
  window.fbq("track", "PageView");
}

function applyConsent(consent) {
  if (consent.analytics) loadGA4();
  if (consent.marketing) loadFacebookPixel();
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const consent = JSON.parse(raw);
        applyConsent(consent);
        return;
      }
    } catch {}
    setVisible(true);
  }, []);

  const saveConsent = (analytics, marketing) => {
    const consent = {
      analytics: !!analytics,
      marketing: !!marketing,
      ts: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {}
    applyConsent(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie samtykke"
    >
      <div className="max-w-3xl mx-auto bg-white shadow-2xl rounded-2xl border border-border/40 p-5 md:p-6 pointer-events-auto">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
            <Cookie className="h-5 w-5 text-secondary" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-primary mb-1">
              Vi bruger cookies
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Vi bruger nødvendige cookies for at siden virker. Med dit samtykke
              bruger vi også cookies til <strong>statistik</strong> og{" "}
              <strong>markedsføring</strong> for at gøre din oplevelse bedre.{" "}
              <Link
                to="/privatlivspolitik"
                className="text-secondary underline hover:no-underline"
                onClick={() => setVisible(false)}
              >
                Læs mere
              </Link>
              .
            </p>
          </div>
          <button
            onClick={() => saveConsent(false, false)}
            aria-label="Luk"
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => saveConsent(false, false)}
            className="flex-1 px-4 py-2.5 rounded-full border border-border bg-white hover:bg-muted/30 text-primary text-sm font-medium transition-colors"
            data-testid="cookie-reject"
          >
            Kun nødvendige
          </button>
          <button
            onClick={() => saveConsent(true, true)}
            className="flex-1 px-4 py-2.5 rounded-full bg-secondary hover:bg-secondary/90 text-primary text-sm font-medium shadow transition-colors"
            data-testid="cookie-accept"
          >
            Acceptér alle
          </button>
        </div>
      </div>
    </div>
  );
}
